import { z } from 'zod';
import { projectPhaseResponseSchema, type ProjectPhaseType } from './phase.schema';
import { projectStatusSchema } from './project.schema';

/**
 * Farbcodierung der Phasentypen im Zeitplan (Gantt-UI und PDF-Export nutzen
 * dieselben Werte, damit Legende und Balken überall gleich aussehen).
 */
export const PHASE_TYPE_COLORS: Record<ProjectPhaseType, string> = {
  PROJECT_HANDOVER: '#64748b', // Grau — Projektübergabe
  ENGINEERING: '#1155a3', // Blau — Konstruktion
  PLAN_APPROVAL: '#0369a1', // Helleres Blau — Planfreigabe (Bürophase wie Engineering)
  MANUFACTURING: '#d97706', // Orange — Fertigung
  PICKING: '#ca8a04', // Ocker — Kommissionierung (Lager)
  TRANSPORT: '#0f766e', // Petrol — Transport
  DELIVERY: '#4338ca', // Indigo — Lieferung
  ASSEMBLY: '#059669', // Grün — Montage
  ACCEPTANCE: '#be123c', // Rot — Abnahme
  PROCUREMENT: '#7c3aed', // Violett — Beschaffung (Altbestand)
  COMMISSIONING: '#0891b2', // Cyan — Inbetriebnahme (Altbestand)
};

/**
 * Bezeichnungen im Zeitplan (Gantt-UI + beide PDF-Exporte). Weicht bei
 * `ENGINEERING` bewusst von `PHASE_TYPE_LABELS` ab: im Zeitplan steht die
 * fachliche Bezeichnung „Konstruktion", in der Phasen-Verwaltung der Typname
 * „Engineering".
 */
export const PHASE_TYPE_SCHEDULE_LABELS: Record<'de' | 'en', Record<ProjectPhaseType, string>> = {
  de: {
    PROJECT_HANDOVER: 'Projektübergabe',
    ENGINEERING: 'Konstruktion',
    PLAN_APPROVAL: 'Planfreigabe',
    MANUFACTURING: 'Fertigung',
    PICKING: 'Kommissionierung',
    TRANSPORT: 'Transport',
    DELIVERY: 'Lieferung',
    ASSEMBLY: 'Montage',
    ACCEPTANCE: 'Abnahme',
    PROCUREMENT: 'Beschaffung',
    COMMISSIONING: 'Inbetriebnahme',
  },
  en: {
    PROJECT_HANDOVER: 'Project handover',
    ENGINEERING: 'Engineering',
    PLAN_APPROVAL: 'Drawing approval',
    MANUFACTURING: 'Manufacturing',
    PICKING: 'Picking',
    TRANSPORT: 'Transport',
    DELIVERY: 'Delivery',
    ASSEMBLY: 'Assembly',
    ACCEPTANCE: 'Acceptance',
    PROCUREMENT: 'Procurement',
    COMMISSIONING: 'Commissioning',
  },
};

/**
 * Projektzeitplan (PM-App Phase C, ADR 0052): projektübergreifende Gantt-Sicht
 * auf die bestehenden Projektphasen. Ein Eintrag je aktivem Projekt mit allen
 * Phasen — Jahres-Zuschnitt (KW-Raster) ist Sache des Clients bzw. des
 * PDF-Exports.
 */
export const scheduleProjectSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  status: projectStatusSchema,
  deliveryDate: z.string().datetime().nullable(),
  /**
   * Ende des Liefertermin-Zeitraums (von–bis), für den Hinweis „Lieferung endet
   * nach dem Liefertermin" beim Verschieben mit Folgephasen. Optional, damit
   * ältere Clients/Fixtures ohne das Feld gültig bleiben.
   */
  deliveryDateEnd: z.string().datetime().nullable().optional(),
  phases: z.array(projectPhaseResponseSchema),
});
export type ScheduleProject = z.infer<typeof scheduleProjectSchema>;

/** Sprache des Zeitplan-Exports (zweisprachig wie der Alt-App-Export). */
export const scheduleExportLanguageSchema = z.enum(['de', 'en']);
export type ScheduleExportLanguage = z.infer<typeof scheduleExportLanguageSchema>;

/** Montag der ISO-Kalenderwoche 1 (die Woche, die den 4. Januar enthält). */
export function isoWeekYearStart(year: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const weekday = jan4.getUTCDay() || 7; // 1 = Montag … 7 = Sonntag
  return new Date(Date.UTC(year, 0, 4 - (weekday - 1)));
}

/** Anzahl ISO-Kalenderwochen des Jahres (52 oder 53). */
export function isoWeeksInYear(year: number): number {
  const msPerWeek = 7 * 86_400_000;
  return Math.round((isoWeekYearStart(year + 1).getTime() - isoWeekYearStart(year).getTime()) / msPerWeek);
}

/**
 * Position eines Phasen-Balkens im KW-Raster eines Jahres als Anteile [0..1]
 * (Gantt-UI und PDF teilen die Geometrie). Fehlt ein Termin, wird eine Woche
 * ab/bis zum vorhandenen Termin angenommen; ganz ohne Termine (oder komplett
 * außerhalb des Jahres) → null. Werte sind auf das Jahr zugeschnitten.
 */
export function scheduleBarMetrics(
  plannedStart: string | null,
  plannedEnd: string | null,
  year: number,
): { startFrac: number; endFrac: number; clippedStart: boolean; clippedEnd: boolean } | null {
  if (!plannedStart && !plannedEnd) return null;
  const msPerDay = 86_400_000;
  const gridStart = isoWeekYearStart(year).getTime();
  const gridEnd = isoWeekYearStart(year + 1).getTime();

  let start = plannedStart ? new Date(plannedStart).getTime() : null;
  let end = plannedEnd ? new Date(plannedEnd).getTime() : null;
  // Fehlender Gegen-Termin: eine Woche annehmen — 6 Tage Spanne, denn der
  // Ende-Tag zählt unten inklusiv dazu (sonst würden 8 Tage gezeichnet).
  if (start === null) start = (end as number) - 6 * msPerDay;
  if (end === null) end = start + 6 * msPerDay;
  // Ende-Tag zählt mit (inklusive): Balken bis zum Folgetag zeichnen.
  end += msPerDay;
  if (end <= start) end = start + msPerDay;

  if (end <= gridStart || start >= gridEnd) return null;
  const clippedStart = start < gridStart;
  const clippedEnd = end > gridEnd;
  const span = gridEnd - gridStart;
  return {
    startFrac: Math.max(0, (start - gridStart) / span),
    endFrac: Math.min(1, (end - gridStart) / span),
    clippedStart,
    clippedEnd,
  };
}

/** Eine Phase, wie die Konflikt-Prüfung sie braucht (Teilmenge der Response). */
export interface SchedulePhaseLike {
  id: string;
  name: string;
  /** Phasentyp — entscheidet, ob die Phase vor oder nach der Lieferung liegt. */
  type?: string;
  sequence: number;
  plannedStart: string | null;
  plannedEnd: string | null;
}

/** Phasentypen, die naturgemäß nach der Lieferung liegen (vor Ort beim Kunden). */
const AFTER_DELIVERY_TYPES: ReadonlySet<string> = new Set(['ASSEMBLY', 'COMMISSIONING', 'ACCEPTANCE']);

/** Tag (UTC) eines ISO-Zeitpunkts — Phasen- und Liefertermine sind Kalendertage. */
const dayOf = (iso: string): string => new Date(iso).toISOString().slice(0, 10);

export interface ScheduleConflict {
  /** Betroffene Phase. */
  phaseId: string;
  message: string;
}

/**
 * Konflikt-Prüfung beim Verschieben von Phasen-Terminen (Entscheidung
 * Tech-Lead 2026-07-16): (1) eine Phase endet nach dem Liefertermin,
 * (2) Phasen desselben Projekts überholen ihre Reihenfolge (eine spätere
 * Phase startet vor einer früheren). Reine Warnungen — gespeichert wird
 * trotzdem, der Benutzer bestätigt.
 *
 * (1) gilt nur für Phasen bis einschließlich der Lieferung (2026-10-06): Montage,
 * Abnahme und alles, was hinter der Lieferphase einsortiert ist, liegt im
 * Standardplan planmäßig danach. Ohne Lieferphase entscheidet der Typ. Maßgeblich
 * ist das Ende des Liefertermins (`deliveryDateEnd`), wenn er ein Zeitraum ist.
 */
export function findScheduleConflicts(
  phases: SchedulePhaseLike[],
  deliveryDate: string | null,
  deliveryDateEnd?: string | null,
): ScheduleConflict[] {
  const conflicts: ScheduleConflict[] = [];
  const deliveryLimit = deliveryDateEnd ?? deliveryDate;
  const lastDay = deliveryLimit ? dayOf(deliveryLimit) : null;
  const ordered = [...phases].sort((a, b) => a.sequence - b.sequence);
  const deliveryPhase = ordered.find((phase) => phase.type === 'DELIVERY');
  const isBeforeDelivery = (phase: SchedulePhaseLike): boolean =>
    deliveryPhase ? phase.sequence <= deliveryPhase.sequence : !AFTER_DELIVERY_TYPES.has(phase.type ?? '');

  for (const phase of ordered) {
    if (lastDay !== null && phase.plannedEnd && isBeforeDelivery(phase) && dayOf(phase.plannedEnd) > lastDay) {
      conflicts.push({
        phaseId: phase.id,
        message: `„${phase.name}" endet nach dem Liefertermin.`,
      });
    }
  }

  // Reihenfolge: Start-Termine müssen der Phasen-Reihenfolge folgen
  // (Überlappung ist erlaubt, Überholen nicht).
  let lastStart: number | null = null;
  let lastName = '';
  for (const phase of ordered) {
    if (!phase.plannedStart) continue;
    const start = new Date(phase.plannedStart).getTime();
    if (lastStart !== null && start < lastStart) {
      conflicts.push({
        phaseId: phase.id,
        message: `„${phase.name}" startet vor „${lastName}" (Reihenfolge überholt).`,
      });
    }
    if (lastStart === null || start > lastStart) {
      lastStart = start;
      lastName = phase.name;
    }
  }

  return conflicts;
}
